import { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Header from './components/Header';
import { PromoMarquee } from '@/components/common/PromoMarquee';
import BottomNav from './components/BottomNav';
import { Footer } from './components/Footer';
import { useAppDispatch, useAppSelector, useIsAuthenticated } from '@/hooks';
import { closeMyBetsModal } from '@/store/slices/currentBetCountSlice';
import { FloatingMyBetsButton } from './components/FloatingMyBetsButton';
import MyBetsModal from '@/pages/MyBetsModal';
import { useGetPendingBetsCountQuery } from '@/store/api/betReportApi';
import MobileScrollTopButton from './components/MobileScrollTopButton';
import { useGetStakeSetQuery } from '@/store/api/stakesApi';
import { FaWhatsapp } from 'react-icons/fa';
import useContactSupport from '@/hooks/useContactSupport';

export const layoutConfig = {
  showPromoMarquee: ['/sports'],
  showBottomNav: ['/casino', '/sports', '/leagues'],
  showFooter: ['/casino', '/sports', '/marketdetails', '/newreferral'],
  hideCasinoLauncher: ['/fullmarket', '/marketdetails'],
};

const isMatchingRoute = (pathname: string, routes: string[]) =>
  routes.some((route) => pathname === route || (route !== '/' && pathname.startsWith(`${route}/`)));

function AppLayoutWithBottomNav() {
  useGetPendingBetsCountQuery();
  useGetStakeSetQuery();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  const isMyBetsModalOpen = useAppSelector((state) => state.currentBetCount.isMyBetsModalOpen);
  const { handlePublicWhatsAppSupportClick } = useContactSupport({
    initialMessage: 'Hello, I am a new user and would like to get an account.',
  });

  const showPromoMarquee = isMatchingRoute(pathname, layoutConfig.showPromoMarquee);
  const showBottomNav = isMatchingRoute(pathname, layoutConfig.showBottomNav);
  const showFooter = isMatchingRoute(pathname, layoutConfig.showFooter);
  // const hideCasinoLauncher = isMatchingRoute(pathname, layoutConfig.hideCasinoLauncher);
  const isHomepage = pathname === '/';

  useEffect(() => {
    return () => {
      if (isMyBetsModalOpen) {
        dispatch(closeMyBetsModal());
      }
    };
  }, [dispatch, isMyBetsModalOpen, pathname]);

  return (
    <div
      style={{ background: `url('/images/bg.jpg')` }}
      className="grid h-[100dvh] w-full grid-cols-[1fr_minmax(0,30rem)_1fr] overflow-hidden !bg-cover !bg-top !bg-no-repeat"
    >
      <div className="flex items-center justify-center max-md:hidden">
        <img src="/images/logo.webp" alt="Logo" className="w-full max-w-xs" />
      </div>

      <main
        data-app-scroll-container
        className="scrollbar-hidden relative col-start-2 col-end-2 overflow-x-hidden overflow-y-scroll bg-[#eef3f7]"
      >
        <Header />

        {showPromoMarquee && <PromoMarquee />}

        <div className="min-h-[100dvh] animate-fade-in">
          <Outlet />
        </div>

        {showFooter && (
          <div className={'pb-28'}>
            <Footer />
          </div>
        )}
      </main>

      <FloatingMyBetsButton />
      {isMyBetsModalOpen && <MyBetsModal />}
      {!isAuthenticated && isHomepage && (
        <button
          type="button"
          onClick={handlePublicWhatsAppSupportClick}
          aria-label="Get an account on WhatsApp"
          className="animate-bounce fixed bottom-[6rem] left-[calc(50%-14.5rem)] z-40 flex size-[4rem] items-center justify-center rounded-full text-white shadow-xl transition-transform hover:scale-105 max-[480px]:left-3.5 lg:bottom-6"
        >
          <img src="/images/get_account.png" alt="Get a new account" className="object-contain" />
          <span className="pointer-events-none absolute left-full top-1/2 ml-3 w-max -translate-y-1/2 rounded-lg bg-[#12263a] px-3 py-2 text-xs font-semibold text-white shadow-lg">
            <span className="absolute -left-1 top-1/2 size-2 -translate-y-1/2 rotate-45 bg-[#12263a]" />
            Click here for a new ID
          </span>
        </button>
      )}

      {isAuthenticated && isHomepage && (
        <Link
          to="/agent-details"
          aria-label="My Agent"
          className="fixed bottom-6 left-[calc(50%-14.5rem)] z-40 max-[480px]:left-3.5 animate-bounce flex items-center transition-transform hover:scale-110"
        >
          <img src="/images/my-agent.png" alt="My Agent" className="size-20 object-contain" />
        </Link>
      )}

      <MobileScrollTopButton />
      {showBottomNav && <BottomNav />}
    </div>
  );
}

export default AppLayoutWithBottomNav;
